"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import posthog from "posthog-js";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { FiAlertCircle, FiRefreshCw } from "react-icons/fi";
import Button from "@/components/ui/button";
import { labQueryOptions } from "@/api/provider/lab/lab-query";
interface LabJunctionEmbedProps {
  orderId: string;
}

// Floor so the frame never collapses to nothing on very short viewports.
const MIN_HEIGHT = 360;
// Matches the patient content area's bottom padding (pb-8 = 2rem) so the frame
// reaches the bottom of the visible scroll area without adding a second scrollbar.
const BOTTOM_GAP = 32;
export default function LabJunctionEmbed(t0) {
  const $ = _c(23);
  const {
    orderId
  } = t0;
  let t1;
  if ($[0] !== orderId) {
    t1 = labQueryOptions.embedUrl(orderId);
    $[0] = orderId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data,
    isLoading,
    isError,
    isFetching,
    refetch
  } = useQuery(t1);
  const url = data?.url;
  const hasCapturedViewRef = useRef(false);
  let t2;
  let t3;
  if ($[2] !== url) {
    t2 = () => {
      if (!url || hasCapturedViewRef.current) {
        return;
      }
      hasCapturedViewRef.current = true;
      posthog.capture("lab_junction_embed_viewed");
    };
    t3 = [url];
    $[2] = url;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const containerRef = useRef(null);
  const [height, setHeight] = useState();
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const el = containerRef.current;
      if (!el) {
        return;
      }
      const measure = () => {
        const top = el.getBoundingClientRect().top;
        setHeight(Math.max(MIN_HEIGHT, window.innerHeight - top - BOTTOM_GAP));
      };
      measure();
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    };
    t5 = [];
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useLayoutEffect(t4, t5);
  let content;
  if (isLoading) {
    let t6;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="h-full animate-pulse rounded-lg bg-muted" />;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    content = t6;
  } else {
    if (isError || !url) {
      let t6;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <FiAlertCircle className="h-8 w-8 text-foreground-muted" />;
        $[8] = t6;
      } else {
        t6 = $[8];
      }
      let t7;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <div className="space-y-1"><p className="text-body-strong text-foreground">Couldn't load the Junction view</p><p className="text-caption text-foreground-secondary">The embed link may have expired. Try minting a fresh one.</p></div>;
        $[9] = t7;
      } else {
        t7 = $[9];
      }
      let t8;
      if ($[10] !== refetch) {
        t8 = () => refetch();
        $[10] = refetch;
        $[11] = t8;
      } else {
        t8 = $[11];
      }
      let t9;
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = <FiRefreshCw className="h-3.5 w-3.5" />;
        $[12] = t9;
      } else {
        t9 = $[12];
      }
      let t10;
      if ($[13] !== isFetching || $[14] !== t8) {
        t10 = <div className="flex h-full flex-col items-center justify-center gap-3 rounded-lg border border-border bg-background-secondary text-center">{t6}{t7}<Button variant="light" size="sm" onClick={t8} isLoading={isFetching} icon={t9}>Retry</Button></div>;
        $[13] = isFetching;
        $[14] = t8;
        $[15] = t10;
      } else {
        t10 = $[15];
      }
      content = t10;
    } else {
      let t6;
      if ($[16] !== url) {
        t6 = <iframe src={url} title="Junction order view" className="h-full w-full rounded-lg border border-border bg-background" sandbox="allow-same-origin allow-scripts allow-popups allow-forms" />;
        $[16] = url;
        $[17] = t6;
      } else {
        t6 = $[17];
      }
      content = t6;
    }
  }
  let t6;
  if ($[18] !== height) {
    t6 = {
      height
    };
    $[18] = height;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== content || $[21] !== t6) {
    t7 = <div ref={containerRef} style={t6}>{content}</div>;
    $[20] = content;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
}